import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchArchives } from '../api/archives';
import { backend, type ProjectSummary } from '../api/backend';
import { editName, parseEditUrl } from '../api/importEdit';
import { formatTime } from '../format/helpers';
import { useSession } from '../state/session';
import './ProjectsPage.css';

export default function ProjectsPage() {
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [renaming, setRenaming] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const { user, logout } = useSession();
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    try {
      setProjects(await backend.projects.list());
      setError(null);
    } catch {
      setError('Could not load projects — is the backend running?');
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function create() {
    const title = newTitle.trim() || 'Untitled project';
    try {
      const p = await backend.projects.create(title);
      navigate(`/edit/${p.id}`);
    } catch {
      setError('Create failed');
    }
  }

  async function importEdit() {
    const url = importUrl.trim();
    if (!url || importing) return;
    setImporting(true);
    try {
      // Validate the URL up front (also derives a sensible project title); the
      // actual clip import runs in the editor once it's loaded.
      const archives = await fetchArchives();
      const { editId } = parseEditUrl(url, archives);
      const p = await backend.projects.create(editName(editId) || 'Imported edit');
      navigate(`/edit/${p.id}`, { state: { importEditUrl: url } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
      setImporting(false);
    }
  }

  async function duplicate(id: number) {
    try {
      await backend.projects.duplicate(id);
      await reload();
    } catch {
      setError('Duplicate failed');
    }
  }

  async function remove(id: number) {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    try {
      await backend.projects.delete(id);
      await reload();
    } catch {
      setError('Delete failed');
    }
  }

  async function commitRename(id: number) {
    const title = renameValue.trim();
    setRenaming(null);
    if (!title) return;
    try {
      await backend.projects.patch(id, { title });
      await reload();
    } catch {
      setError('Rename failed');
    }
  }

  return (
    <div className="projects-page">
      <header className="projects-header">
        <h1>
          fabled<span>editor</span>
        </h1>
        <div className="projects-user">
          <a href="/guide" target="_blank" rel="noopener" className="projects-guide">
            User Guide
          </a>
          {user && <span>{user.username}</span>}
          <button className="ghost" onClick={() => void logout().then(() => navigate('/login'))}>
            Log out
          </button>
        </div>
      </header>

      <div className="projects-body">
        <div className="projects-new">
          <input
            placeholder="New project title…"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void create()}
          />
          <button className="primary" onClick={() => void create()}>
            Create project
          </button>
        </div>

        <div className="projects-new projects-import">
          <input
            placeholder="…or paste a pan.do/ra edit URL to import"
            value={importUrl}
            disabled={importing}
            onChange={(e) => setImportUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void importEdit()}
          />
          <button className="ghost" onClick={() => void importEdit()} disabled={importing || !importUrl.trim()}>
            {importing ? 'Importing…' : 'Import edit'}
          </button>
        </div>

        {error && <div className="projects-error">{error}</div>}
        {projects === null && <div className="projects-empty">Loading…</div>}
        {projects?.length === 0 && !error && (
          <div className="projects-empty">No projects yet — create one above.</div>
        )}

        <div className="projects-grid">
          {projects?.map((p) => (
            <div className="project-card" key={p.id}>
              <Link to={`/edit/${p.id}`} className="project-card-main">
                {renaming === p.id ? (
                  <input
                    autoFocus
                    value={renameValue}
                    onClick={(e) => e.preventDefault()}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => void commitRename(p.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void commitRename(p.id);
                      if (e.key === 'Escape') setRenaming(null);
                    }}
                  />
                ) : (
                  <h2>{p.title}</h2>
                )}
                <div className="project-card-meta">
                  <span className="timecode">{formatTime(p.duration ?? 0)}</span>
                  <span>modified {new Date(p.modified).toLocaleDateString()}</span>
                </div>
                {p.description && <p className="project-card-desc">{p.description}</p>}
              </Link>
              <div className="project-card-actions">
                <button
                  className="ghost"
                  onClick={() => {
                    setRenaming(p.id);
                    setRenameValue(p.title);
                  }}
                >
                  Rename
                </button>
                <button className="ghost" onClick={() => void duplicate(p.id)}>
                  Duplicate
                </button>
                <button className="ghost danger" onClick={() => void remove(p.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
